import {
  buildLoginLanguageChoice,
  buildSwitchLanguageChoice,
  normalizeLanguageChoice,
} from '@/shared/i18n/language-choice';

const ID_LANGUAGE_KEY = 'st_language';
const ID_LANGUAGE_CHOICE_KEY = 'st_language_choice';
const ID_LANGUAGE_NOTICE_KEY = 'st_language_notice';

const i18nService = {
  defaultLanguage: 'ro',

  languages: [
    {
      lang: 'ro',
      name: 'Română',
      flag: `${process.env.BASE_URL}media/svg/flags/109-romania.svg`,
    },
    {
      lang: 'hu',
      name: 'Magyar',
      flag: `${process.env.BASE_URL}media/svg/flags/115-hungary.svg`,
    },
    {
      lang: 'en',
      name: 'English',
      flag: `${process.env.BASE_URL}media/svg/flags/260-united-kingdom.svg`,
    },
  ],

  /**
     * Keep the active language in the localStorage
     * @param lang
     */
  setActiveLanguage(lang) {
    localStorage.setItem(ID_LANGUAGE_KEY, lang);
    this.syncDocumentLanguage(lang);
  },

  /**
     * Keep <html lang> in step with the active language. Left stale, the page
     * claims Romanian while showing Hungarian, which is what makes the browser
     * offer to machine-translate it.
     * @param lang
     */
  syncDocumentLanguage(lang) {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('lang', lang || this.getActiveLanguage());
  },

  /**
     * Get the current active language
     * @returns {string | string}
     */
  getActiveLanguage() {
    return this.getStoredLanguage() || this.defaultLanguage;
  },

  /**
     * Get the language chosen on this device, or null when none was
     * @returns {string | null}
     */
  getStoredLanguage() {
    const stored = localStorage.getItem(ID_LANGUAGE_KEY);

    return this.isSupportedLanguage(stored) ? stored : null;
  },

  /**
     * @param lang
     * @returns {boolean}
     */
  isSupportedLanguage(lang) {
    return this.languages.some((language) => language.lang === lang);
  },

  /**
     * The question to ask after a log-in whose screen language is not the account language, or null
     * @param accountLanguage
     * @returns {{mode: string, current: string, proposed: string} | null}
     */
  getLoginLanguageChoice(accountLanguage) {
    return buildLoginLanguageChoice({
      accountLanguage,
      screenLanguage: this.getActiveLanguage(),
      supported: this.getLanguageCodes(),
      fallback: this.defaultLanguage,
    });
  },

  /**
     * The confirmation to ask before switching away from the language on screen, or null
     * @param nextLanguage
     * @returns {{mode: string, current: string, proposed: string} | null}
     */
  getSwitchLanguageChoice(nextLanguage) {
    return buildSwitchLanguageChoice({
      currentLanguage: this.getActiveLanguage(),
      nextLanguage,
      supported: this.getLanguageCodes(),
    });
  },

  /**
     * Keep an unanswered language choice for the rest of the browser session. Never throws: it runs on logout.
     * @param choice
     */
  storeLanguageChoice(choice) {
    try {
      if (choice) {
        sessionStorage.setItem(ID_LANGUAGE_CHOICE_KEY, JSON.stringify(choice));
      } else {
        sessionStorage.removeItem(ID_LANGUAGE_CHOICE_KEY);
      }
    } catch (error) {
      console.error('[language] choice storage failed', error?.message);
    }
  },

  /**
     * @returns {{mode: string, current: string, proposed: string} | null}
     */
  getStoredLanguageChoice() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(ID_LANGUAGE_CHOICE_KEY));

      return normalizeLanguageChoice(stored, this.getLanguageCodes());
    } catch (error) {
      return null;
    }
  },

  /**
     * Confirm a language switch to the user on the page load that follows it
     * @param lang
     */
  setLanguageNotice(lang) {
    try {
      sessionStorage.setItem(ID_LANGUAGE_NOTICE_KEY, lang);
    } catch (error) {
      console.error('[language] notice storage failed', error?.message);
    }
  },

  /**
     * @returns {string | null}
     */
  takeLanguageNotice() {
    try {
      const notice = sessionStorage.getItem(ID_LANGUAGE_NOTICE_KEY);
      sessionStorage.removeItem(ID_LANGUAGE_NOTICE_KEY);

      return this.isSupportedLanguage(notice) ? notice : null;
    } catch (error) {
      return null;
    }
  },

  getLanguageCodes() {
    return this.languages.map((language) => language.lang);
  },
};

export default i18nService;
